import type { ModeOutcome } from "@highmarch/app";
import { fmtCompact } from "../kit";
import { rowHtml } from "../components";
import { marksGlyph, rewardWordsHtml } from "../hub/eventRewards";
import { paintedIconHtml } from "../featureIcons";
import { essenceIconHtml } from "../forgeArt";
import { gemGlyph } from "../hub/eventRewards";
import { chestIconUrl } from "../portraits";
import { RARITY_IDS } from "@highmarch/domain";
import type { EndlessChestBand } from "@highmarch/domain";

/** The week's chest art at the head of its row (the plain chest under the first band). */
function paintedChest(b: EndlessChestBand | null): string {
  return `<img decoding="sync" src="${chestIconUrl(b?.reward.chest !== null && b?.reward.chest !== undefined ? RARITY_IDS[b.reward.chest] : "common")}" alt="" style="height:${LEAD_PX}px${b ? "" : ";filter:grayscale(1);opacity:.6"}">`;
}

/** The painted lead of each end-screen row, and Endless's loop beside its score (CSS px). */
const LEAD_PX = 28, SCORE_ICON_PX = 40;

/**
 * The end screen's mode block (docs/MODES.md): an Iron run's star and Marks (or that today's try is spent); an Endless
 * run's score against this week's and the all-time best — NEW BEST stamped — and where the week's chest stands.
 * A fact, never an offer.
 */
export function modeEndHtml(o: ModeOutcome, level: number, won: boolean): string {
  const row = (kind: string, lead: string, title: string, sub: string, end = ""): string => rowHtml({ lead, title, sub, end, compact: true, className: `erow ${kind}` });
  if (o.kind === "iron") {
    // A paid repeat win also pays gems and Forge Essence (gear v2's free routes): the same chips as the Marks.
    const extra = `${o.ironGems > 0 ? `<small class="short">${gemGlyph(14)}+${o.ironGems}</small>` : ""}${o.ironEssence > 0 ? `<small class="short ess">${essenceIconHtml(14)}+${fmtCompact(o.ironEssence)}</small>` : ""}`;
    const paid = (marks: number) => `${marks > 0 ? `<small class="short">${marksGlyph(14)}+${fmtCompact(marks)}</small>` : ""}${extra}`;
    const r = o.ironFirst
      ? row("iron first", paintedIconHtml("iron", LEAD_PX), "IRON STAR", `Level ${level} · +${fmtCompact(o.ironMarks)} Marks`, paid(o.ironMarks))
      : won
        ? row("iron", paintedIconHtml("iron", LEAD_PX), "IRON", o.ironMarks > 0 ? `Won again · today's Marks${o.ironGems > 0 ? ", gems" : ""}${o.ironEssence > 0 ? " and essence" : ""}` : "Won again: today's reward was already paid", paid(o.ironMarks))
        : row("iron lost", paintedIconHtml("iron", LEAD_PX), "IRON", "Try spent · back tomorrow");
    return `<div class="evend modeend">${r}</div>`;
  }
  if (o.kind === "endless" && o.endless) {
    const e = o.endless;
    const stamp = e.newBest ? `<b class="enstamp disp">NEW BEST!</b>` : e.newWeekBest ? `<b class="enstamp week disp">BEST THIS WEEK</b>` : "";
    const best = Math.max(e.previousBest.waves, e.waves), weekBest = Math.max(e.previousWeek.waves, e.waves);
    const prev = e.newBest && e.previousBest.waves > 0 ? `All time ${best} (was ${e.previousBest.waves})` : `All time ${best}`;
    const chest = e.band ? rewardWordsHtml(e.band.reward) : "None yet";
    const next = e.nextBand ? `next at wave ${e.nextBand.fromWave}` : "the top chest";
    return `<div class="evend modeend stack">
      <div class="enscore">${stamp}<span class="ic">${paintedIconHtml("endless", SCORE_ICON_PX)}</span><b class="disp">${e.waves}</b><small>wave${e.waves === 1 ? "" : "s"} · ${fmtCompact(e.points)} points</small></div>
      ${row("enrec", paintedIconHtml("endless", LEAD_PX), `Week best ${weekBest}`, prev)}
      ${row("enweek", paintedChest(e.band), chest, `Weekly chest · ${next}`)}
    </div>`;
  }
  return "";
}
